import { createFileRoute } from '@tanstack/react-router'
import { Helmet } from 'react-helmet-async'

export const Route = createFileRoute(
    '/(onboarding)/(documents)/extension-privacy-policy'
)({
    component: ExtensionPrivacyPolicy,
})

function ExtensionPrivacyPolicy() {
    return (
        <>
            <Helmet>
                <title>Chrome Extension Privacy Policy - What The F*** Are You Wearing</title>
                <meta
                    name="description"
                    content="Privacy Policy for What The F*** Are You Wearing Chrome Extension"
                />
            </Helmet>

            <div className="glass-background min-h-dvh pt-safe">
                <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 lg:px-8">
                    {/* Header */}
                    <div className="mb-12 text-center">
                        <h1 className="text-4xl font-bold text-gray-900 sm:text-5xl">
                            Privacy Policy for Chrome Extension
                        </h1>
                        <p className="mt-4 text-xl text-gray-600">
                            Effective Date: 5 November 2025
                        </p>
                        <div className="mx-auto mt-4 h-1 w-24 rounded-full bg-gradient-to-r from-blue-500 to-purple-500" />
                    </div>

                    {/* Introduction */}
                    <div className="mb-8 rounded-2xl border border-gray-100 bg-white/80 p-6 shadow-sm backdrop-blur-sm sm:p-8">
                        <div className="space-y-3 leading-relaxed text-gray-700">
                            <p>
                                This Privacy Policy explains how{' '}
                                <strong>Softwear Labs Pty Ltd</strong> ("we",
                                "our", or "us") collects, uses, and protects
                                your personal information when you use the What
                                The F*** Are You Wearing ("WTFAYW") Chrome
                                Extension (the "Extension").
                            </p>
                            <p className="rounded border-l-4 border-blue-500 bg-blue-50 p-4 text-sm">
                                <strong>
                                    By installing and using the Extension, you
                                    agree to the collection and use of your
                                    information in accordance with this policy.
                                </strong>
                            </p>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="space-y-8">
                        {/* 1. Information We Collect */}
                        <section className="rounded-2xl border border-gray-100 bg-white/80 p-6 shadow-sm backdrop-blur-sm sm:p-8">
                            <h2 className="mb-4 flex items-center gap-2 text-2xl font-bold text-gray-900">
                                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-100 text-sm font-bold text-blue-600">
                                    1
                                </span>
                                Information We Collect
                            </h2>
                            <p className="mb-4 text-gray-700">
                                When you install and use the Extension, we
                                collect the following types of information:
                            </p>
                            <ul className="space-y-3">
                                <li className="flex gap-3">
                                    <span className="mt-1 text-blue-500">•</span>
                                    <div>
                                        <strong className="text-gray-900">
                                            Account Information:
                                        </strong>
                                        <span className="text-gray-700">
                                            {' '}
                                            Your name, email address, and account
                                            information that you use to register
                                            and interact with the WTFAYW
                                            platform.
                                        </span>
                                    </div>
                                </li>
                                <li className="flex gap-3">
                                    <span className="mt-1 text-blue-500">•</span>
                                    <div>
                                        <strong className="text-gray-900">
                                            Usage Data:
                                        </strong>
                                        <span className="text-gray-700">
                                            {' '}
                                            Information about how you interact
                                            with the Extension, such as the
                                            products you view, save to your
                                            collection, or select for AI try-on,
                                            and the corresponding actions taken
                                            in the Extension.
                                        </span>
                                    </div>
                                </li>
                                <li className="flex gap-3">
                                    <span className="mt-1 text-blue-500">•</span>
                                    <div>
                                        <strong className="text-gray-900">
                                            Browser Information:
                                        </strong>
                                        <span className="text-gray-700">
                                            {' '}
                                            Basic data such as browser type,
                                            operating system, and device
                                            information that may be used for
                                            troubleshooting and improving the
                                            functionality of the Extension.
                                        </span>
                                    </div>
                                </li>
                            </ul>
                        </section>

                        {/* 2. How We Use Your Information */}
                        <section className="rounded-2xl border border-gray-100 bg-white/80 p-6 shadow-sm backdrop-blur-sm sm:p-8">
                            <h2 className="mb-4 flex items-center gap-2 text-2xl font-bold text-gray-900">
                                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-purple-100 text-sm font-bold text-purple-600">
                                    2
                                </span>
                                How We Use Your Information
                            </h2>
                            <p className="mb-4 text-gray-700">
                                We use the information we collect for the
                                following purposes:
                            </p>
                            <ul className="space-y-2 text-gray-700">
                                <li className="flex gap-3">
                                    <span className="mt-1 text-purple-500">
                                        ✓
                                    </span>
                                    <span>
                                        To enable the functionality of the
                                        Extension, including saving products to
                                        collections and generating AI-powered
                                        try-on results.
                                    </span>
                                </li>
                                <li className="flex gap-3">
                                    <span className="mt-1 text-purple-500">
                                        ✓
                                    </span>
                                    <span>
                                        To communicate with you regarding
                                        updates, support, or changes to the
                                        Extension.
                                    </span>
                                </li>
                                <li className="flex gap-3">
                                    <span className="mt-1 text-purple-500">
                                        ✓
                                    </span>
                                    <span>
                                        To improve the user experience and
                                        develop new features for the Extension.
                                    </span>
                                </li>
                                <li className="flex gap-3">
                                    <span className="mt-1 text-purple-500">
                                        ✓
                                    </span>
                                    <span>
                                        To monitor and analyze usage trends to
                                        improve the Extension's performance and
                                        reliability.
                                    </span>
                                </li>
                            </ul>
                        </section>

                        {/* 3. How We Share Your Information */}
                        <section className="rounded-2xl border border-gray-100 bg-white/80 p-6 shadow-sm backdrop-blur-sm sm:p-8">
                            <h2 className="mb-4 flex items-center gap-2 text-2xl font-bold text-gray-900">
                                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-green-100 text-sm font-bold text-green-600">
                                    3
                                </span>
                                How We Share Your Information
                            </h2>
                            <p className="mb-4 text-gray-700">
                                We may share your personal data with the
                                following third parties:
                            </p>
                            <ul className="mb-4 space-y-2 text-gray-700">
                                <li className="flex gap-3">
                                    <span className="mt-1 text-green-500">
                                        •
                                    </span>
                                    <span>
                                        <strong>Google:</strong> For processing
                                        AI-powered Try-On features and other
                                        service functionalities.
                                    </span>
                                </li>
                                <li className="flex gap-3">
                                    <span className="mt-1 text-green-500">
                                        •
                                    </span>
                                    <span>
                                        <strong>Stripe:</strong> For processing
                                        payments and managing subscriptions.
                                    </span>
                                </li>
                                <li className="flex gap-3">
                                    <span className="mt-1 text-green-500">
                                        •
                                    </span>
                                    <span>
                                        <strong>Google Analytics:</strong> For
                                        tracking user interactions and improving
                                        the Extension.
                                    </span>
                                </li>
                                <li className="flex gap-3">
                                    <span className="mt-1 text-green-500">
                                        •
                                    </span>
                                    <span>
                                        <strong>
                                            Other service providers:
                                        </strong>{' '}
                                        We may share data with trusted
                                        third-party vendors who assist in
                                        providing services like hosting,
                                        technical support, and analytics.
                                    </span>
                                </li>
                            </ul>
                            <p className="rounded border-l-4 border-green-500 bg-green-50 p-4 text-sm font-medium text-green-900">
                                We will never sell, rent, or lease your personal
                                data to third parties.
                            </p>
                        </section>

                        {/* 4. Data Retention */}
                        <section className="rounded-2xl border border-gray-100 bg-white/80 p-6 shadow-sm backdrop-blur-sm sm:p-8">
                            <h2 className="mb-4 flex items-center gap-2 text-2xl font-bold text-gray-900">
                                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-orange-100 text-sm font-bold text-orange-600">
                                    4
                                </span>
                                Data Retention
                            </h2>
                            <div className="space-y-3 text-gray-700">
                                <p>
                                    We retain your personal data for as long as
                                    necessary to provide the Extension's services
                                    or as required by law. You may delete your
                                    personal information at any time by
                                    uninstalling the Extension or managing your
                                    profile settings within the WTFAYW platform.
                                </p>
                            </div>
                        </section>

                        {/* 5. Currency and Product Information Accuracy */}
                        <section className="rounded-2xl border border-amber-100 bg-gradient-to-br from-amber-50 to-orange-50 p-6 shadow-sm sm:p-8">
                            <h2 className="mb-4 flex items-center gap-2 text-2xl font-bold text-gray-900">
                                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-amber-100 text-sm font-bold text-amber-700">
                                    5
                                </span>
                                Currency and Product Information Accuracy
                            </h2>
                            <div className="space-y-3 text-gray-700">
                                <p>
                                    All product and pricing information displayed
                                    via the Extension is provided by the user or
                                    the connected retailer. Softwear Labs makes no
                                    guarantees as to the accuracy of this data.
                                    Prices and product details are provided as a
                                    guide and may not be exact.
                                </p>
                                <p>
                                    We also provide a currency conversion feature
                                    based on real-time exchange rates, but the
                                    converted prices are estimates and not
                                    definitive.
                                </p>
                            </div>
                        </section>

                        {/* 6. Security */}
                        <section className="rounded-2xl border border-gray-100 bg-white/80 p-6 shadow-sm backdrop-blur-sm sm:p-8">
                            <h2 className="mb-4 flex items-center gap-2 text-2xl font-bold text-gray-900">
                                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-red-100 text-sm font-bold text-red-600">
                                    6
                                </span>
                                Security
                            </h2>
                            <div className="space-y-3 text-gray-700">
                                <p>
                                    We take appropriate measures to protect your
                                    data from unauthorized access, alteration, or
                                    disclosure. However, no method of data
                                    transmission over the internet or electronic
                                    storage is 100% secure.
                                </p>
                                <p className="rounded border-l-4 border-amber-500 bg-amber-50 p-4 text-sm text-amber-900">
                                    <strong>Important:</strong> While we strive
                                    to use commercially acceptable means to
                                    protect your personal data, we cannot
                                    guarantee its absolute security.
                                </p>
                            </div>
                        </section>

                        {/* 7. Your Rights and Choices */}
                        <section className="rounded-2xl border border-blue-100 bg-gradient-to-br from-blue-50 to-indigo-50 p-6 shadow-sm sm:p-8">
                            <h2 className="mb-4 flex items-center gap-2 text-2xl font-bold text-gray-900">
                                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-100 text-sm font-bold text-blue-600">
                                    7
                                </span>
                                Your Rights and Choices
                            </h2>
                            <p className="mb-4 font-medium text-gray-700">
                                You have the following rights regarding your
                                personal data:
                            </p>
                            <ul className="mb-4 space-y-2 text-gray-700">
                                <li className="flex gap-3">
                                    <span className="mt-1 text-blue-500">✓</span>
                                    <div>
                                        <strong className="text-gray-900">
                                            Access and Update:
                                        </strong>
                                        <span>
                                            {' '}
                                            You can access, correct, or update
                                            your personal information at any time
                                            via your profile settings on the
                                            WTFAYW platform.
                                        </span>
                                    </div>
                                </li>
                                <li className="flex gap-3">
                                    <span className="mt-1 text-blue-500">✓</span>
                                    <div>
                                        <strong className="text-gray-900">
                                            Data Deletion:
                                        </strong>
                                        <span>
                                            {' '}
                                            You can delete your account or any
                                            uploaded product or AI try-on results
                                            by uninstalling the Extension or
                                            managing your settings within your
                                            account on the WTFAYW platform.
                                        </span>
                                    </div>
                                </li>
                                <li className="flex gap-3">
                                    <span className="mt-1 text-blue-500">✓</span>
                                    <div>
                                        <strong className="text-gray-900">
                                            Opt-Out of Communications:
                                        </strong>
                                        <span>
                                            {' '}
                                            You can opt out of receiving
                                            promotional emails and
                                            communications by using the
                                            unsubscribe feature in the email or
                                            by adjusting your notification
                                            settings within the platform.
                                        </span>
                                    </div>
                                </li>
                                <li className="flex gap-3">
                                    <span className="mt-1 text-blue-500">✓</span>
                                    <div>
                                        <strong className="text-gray-900">
                                            Data Portability:
                                        </strong>
                                        <span>
                                            {' '}
                                            You may request a copy of your data
                                            in a portable format by contacting
                                            us at{' '}
                                            <a
                                                href="mailto:support@wtf.style"
                                                className="font-medium text-blue-600 underline hover:text-blue-700"
                                            >
                                                support@wtf.style
                                            </a>
                                            .
                                        </span>
                                    </div>
                                </li>
                            </ul>
                            <p className="text-sm text-gray-600 italic">
                                Requests regarding your data will be processed
                                in accordance with applicable data protection
                                laws.
                            </p>
                        </section>

                        {/* 8. Children and Minors */}
                        <section className="rounded-2xl border border-gray-100 bg-white/80 p-6 shadow-sm backdrop-blur-sm sm:p-8">
                            <h2 className="mb-4 flex items-center gap-2 text-2xl font-bold text-gray-900">
                                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-pink-100 text-sm font-bold text-pink-600">
                                    8
                                </span>
                                Children and Minors
                            </h2>
                            <div className="space-y-3 text-gray-700">
                                <p>
                                    The Extension is intended for users aged{' '}
                                    <strong>13 years or older</strong>.
                                </p>
                                <p>
                                    If you are under the age of 18, you must
                                    obtain parental or guardian consent before
                                    using the Extension or uploading any content.
                                    We do not knowingly collect or solicit
                                    personal information from anyone under the
                                    age of 13. If we learn that we have
                                    collected personal information from a child
                                    under 13 without verification of parental
                                    consent, we will take steps to delete that
                                    information.
                                </p>
                            </div>
                        </section>

                        {/* 9. Data Transfers */}
                        <section className="rounded-2xl border border-gray-100 bg-white/80 p-6 shadow-sm backdrop-blur-sm sm:p-8">
                            <h2 className="mb-4 flex items-center gap-2 text-2xl font-bold text-gray-900">
                                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-teal-100 text-sm font-bold text-teal-600">
                                    9
                                </span>
                                Data Transfers
                            </h2>
                            <div className="space-y-3 text-gray-700">
                                <p>
                                    Your personal data may be transferred to, and
                                    maintained on, computers located outside of
                                    your country or jurisdiction, where the data
                                    protection laws may differ.
                                </p>
                                <p>
                                    By using the Extension, you consent to this
                                    transfer of data.
                                </p>
                            </div>
                        </section>

                        {/* 10. Changes to This Privacy Policy */}
                        <section className="rounded-2xl border border-gray-100 bg-white/80 p-6 shadow-sm backdrop-blur-sm sm:p-8">
                            <h2 className="mb-4 flex items-center gap-2 text-2xl font-bold text-gray-900">
                                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-violet-100 text-sm font-bold text-violet-600">
                                    10
                                </span>
                                Changes to This Privacy Policy
                            </h2>
                            <div className="space-y-3 text-gray-700">
                                <p>
                                    We may update this Privacy Policy from time
                                    to time. If we make significant changes to
                                    how we handle your data, we will notify you
                                    through the Extension or via email.
                                </p>
                                <p>
                                    The most current version will always be
                                    available on our website and through the
                                    Chrome Web Store.
                                </p>
                            </div>
                        </section>

                        {/* 11. Contact Us */}
                        <section className="rounded-2xl bg-gradient-to-br from-gray-900 to-gray-800 p-6 text-white shadow-lg sm:p-8">
                            <h2 className="mb-4 flex items-center gap-2 text-2xl font-bold">
                                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white text-sm font-bold text-gray-900">
                                    11
                                </span>
                                Contact Us
                            </h2>
                            <p className="mb-4 text-gray-200">
                                If you have any questions about this Privacy
                                Policy or wish to exercise any of your rights,
                                please contact us at:
                            </p>
                            <div className="space-y-2 rounded-xl bg-white/10 p-6 backdrop-blur-sm">
                                <p className="text-lg font-bold">
                                    Softwear Labs Pty Ltd
                                </p>
                                <p className="text-gray-200">
                                    Suite 2, Level 3 7-15 Macquarie Place
                                    <br />
                                    Sydney 2000 NSW, Australia
                                </p>
                                <p className="pt-2">
                                    <span className="text-gray-300">
                                        Email:{' '}
                                    </span>
                                    <a
                                        href="mailto:support@wtf.style"
                                        className="font-medium text-blue-300 underline hover:text-blue-200"
                                    >
                                        support@wtf.style
                                    </a>
                                </p>
                            </div>
                        </section>

                        {/* Footer */}
                        <div className="border-t border-gray-200 pt-8 pb-4">
                            <p className="rounded-xl bg-gray-50 p-6 text-center text-sm text-gray-600">
                                By installing and using the What The F*** Are
                                You Wearing Chrome Extension, you agree to this
                                privacy policy.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}
